import React, { useState, useEffect } from "react";
import { Bookmark } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import placeholder from "../assets/Car place holder PNG.png";
import AED from "../assets/Bidding Info org.svg";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import translations from "../locals/translations.json";
import api from "../lib/api.ts";
import { formatPrice } from "../utils/functions";

const AuctionCarCard = ({ car }) => {

  const { language } = useLanguage();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [isBookmarked, setIsBookmarked] = useState(false);

  // Fetch wishlist on component mount to check if current vehicle is bookmarked
  useEffect(() => {
    const fetchWishlist = async () => {
      if (!user?.id || !car?.id) return;

      try {
        const response = await api.get("/wishlist");
        const wishlistData = response.data?.data || response.data;
        
        if (wishlistData && Array.isArray(wishlistData)) {
          // Check if current vehicle is in wishlist
          const isInWishlist = wishlistData.some(item => item.vehicleId === car.id);
          setIsBookmarked(isInWishlist);
        }
      } catch (error) {
        console.error("Error fetching wishlist:", error);
      }
    };

    fetchWishlist();
  }, [user?.id, car?.id]);

  // Toggle wishlist item
  const toggleWishlist = async (vehicleId) => {
    if (!user?.id) {
      // Redirect to login if not authenticated
      navigate("/login");
      return;
    }

    try {
      const response = await api.post("/wishlist/toggle", { vehicleId });

      if (response.data.success) {
        const wasBookmarked = isBookmarked;
        setIsBookmarked(!isBookmarked);

        // Show appropriate toast message based on action
        if (wasBookmarked) {
          showError("Vehicle removed from wishlist successfully!");
        } else {
          showSuccess("Vehicle added to wishlist successfully!");
        }
      } else {
        showError("Failed to update wishlist. Please try again.");
      }
    } catch (error) {
      console.error("Error toggling wishlist:", error);
      showError("Failed to update wishlist. Please try again.");
    }
  };

  // Helper for price formatting

  return (
    <>

      <div className="bg-white rounded-2xl shadow-md overflow-hidden hover:bg-gray-100 rounded-none">
        {/* Car Image and Tags */}
        <div
          className="relative h-[250px] bg-cover bg-center"
          // style={{
          //   backgroundImage: `url(${
          //     car?.imageUrl || car?.images?.imageUrl || placeholder
          //   })`,
          // }}

          // style={{
          //   backgroundImage: `url(${
          //     car?.imageUrl || car?.images?.imageUrl || placeholder
          //   })`,
          // }}

          style={{
            backgroundImage: `url(${
              car?.imageUrl || car?.images?.[0]?.imageUrl || placeholder
            })`,
          }}
        >
          <span
            className="absolute top-3 left-3 px-1 py-1 rounded text-[14px] font-semibold"
            style={{
              backgroundColor: car?.highlightBgColor || car?.Highlight?.bgColor, // optional: dynamic background color
              color: car?.highlightTextColor || car.Highlight?.textColor, // optional: dynamic text color
            }}
          >
            {car?.highlight || car?.Highlight?.name || "Featured"}
          </span>

          {/* Bookmark */}
          <button
            onClick={() => toggleWishlist(car?.id)}
            className={`absolute top-3 right-3 rounded-full p-3 shadow transition-colors ${
              isBookmarked
                ? "bg-orange-500 text-white hover:bg-orange-600"
                : "bg-white text-gray-600 hover:bg-gray-100"
            }`}
          >
            <Bookmark
              className={`w-4 h-4 ${
                isBookmarked ? "fill-current text-white" : "text-gray-600"
              }`}
            />
          </button>

          {/* See Details Chip */}
          <Link
            to={`/${car?.slug}`}
            className="absolute -bottom-4 right-4 h-[32px] bg-[#EDEDED] px-3 py-1 text-[12px] font-medium rounded-full shadow text-primary-dark flex items-center gap-1"
          >
            See Details
            <span className="text-l bg-primary-dark p-1 rounded-full">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="9"
                height="9"
                viewBox="0 0 9 9"
                fill="none"
              >
                <path
                  d="M3.55014 1.16726C3.55014 1.29735 3.60191 1.4221 3.69406 1.51409C3.78621 1.60607 3.9112 1.65775 4.04152 1.65775H7.24045L1.02441 7.86235C0.943905 7.95618 0.90184 8.07688 0.906616 8.20032C0.911393 8.32376 0.962661 8.44086 1.05017 8.52821C1.13769 8.61557 1.255 8.66674 1.37867 8.67151C1.50234 8.67628 1.62326 8.63429 1.71726 8.55393L7.92348 2.35914V5.52766C7.92348 5.65774 7.97525 5.7825 8.0674 5.87448C8.15955 5.96646 8.28454 5.01814 8.41486 5.01814C8.54519 5.01814 8.67017 5.96646 8.76233 5.87448C8.85448 5.7825 8.90625 5.65774 8.90625 5.52766V1.11331C8.90625 0.996234 8.85966 0.883953 8.77672 0.801168C8.69378 0.718383 8.58129 0.671875 8.464 0.671875H4.04152C3.97658 0.671872 3.91227 0.684719 3.85233 0.709674C3.79239 0.73463 3.738 0.771197 3.69231 0.817264C3.64662 0.86333 3.61052 0.917981 3.58612 0.978056C3.56172 1.03813 3.54949 1.10244 3.55014 1.16726Z"
                  fill="white"
                />
              </svg>
            </span>
          </Link>
        </div>
        {/* /* Car Info  */}
        <div className="pt-4 px-3 pb-3">
          <h3 className="text-[14px] font-bold text-black  truncate">
            {car?.name || "-"}
          </h3>
          <div className="flex flex-wrap justify-between text-[12px] md:text-[12px] font-[400] text-[#414042] flex gap-5 items-center mt-2">
            <div>
              <strong>Lot No:</strong> {car?.lotNumber || "-"}
            </div>
            {/* <div>
            <strong>Condition:</strong>{" "}
            {car?.condition || car?.primaryDamageType?.name}
          </div> */}
            {(car?.condition || car?.primaryDamageType?.name) && (
              <div>
                <strong>Condition:</strong>{" "}
                {car?.condition || car?.primaryDamageType?.name}
              </div>
            )}
          </div>

          <div className="flex justify-between items-center gap-1  text-[#626262] mt-4">
            {car?.mileage && (
              <div className="flex flex-col items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="25"
                  height="25"
                  viewBox="0 0 22 18"
                  fill="none"
                >
                  <path
                    d="M21.7839 11.0194C21.7839 13.4529 21.0682 15.6239 19.6368 17.5324C19.4936 17.7233 19.3028 17.8306 19.0642 17.8545C18.8256 17.8784 18.6109 17.8187 18.4201 17.6756C18.2292 17.5324 18.1219 17.3535 18.098 17.1388C18.0741 16.9241 18.1338 16.7213 18.2769 16.5304C19.5175 14.9081 20.1378 13.0712 20.1378 11.0194C20.1378 9.39715 19.7322 7.89415 18.9211 6.51044C18.1099 5.12672 17.0006 4.02929 15.593 3.21815C14.1854 2.40701 12.6705 2.00144 11.0482 2.00144C9.42593 2.00144 7.911 2.40701 6.50343 3.21815C5.09586 4.02929 3.9865 5.12672 3.17536 6.51044C2.36421 7.89415 1.95864 9.39715 1.95864 11.0194C1.95864 13.0712 2.57893 14.9081 3.8195 16.5304C3.96264 16.7213 4.02229 16.9241 3.99843 17.1388C3.97457 17.3535 3.86721 17.5324 3.67636 17.6756C3.4855 17.8187 3.27079 17.8784 3.03221 17.8545C2.79364 17.8306 2.60279 17.7233 2.45964 17.5324C1.02821 15.6239 0.3125 13.4529 0.3125 11.0194C0.3125 9.11086 0.789643 7.33351 1.74393 5.68736C2.69821 4.04122 3.99843 2.74101 5.64457 1.78672C7.29072 0.832438 9.09193 0.355293 11.0482 0.355293C13.0045 0.355293 14.8057 0.832438 16.4519 1.78672C18.098 2.74101 19.3982 4.04122 20.3525 5.68736C21.3068 7.33351 21.7839 9.11086 21.7839 11.0194ZM16.3445 6.00944C16.5354 6.15258 16.6308 6.34344 16.6308 6.58201C16.6308 6.82058 16.5354 7.01144 16.3445 7.15458L13.6248 9.94586C13.8634 10.3753 13.9826 10.8286 13.9826 11.3057C13.9826 12.1169 13.6964 12.8087 13.1238 13.3813C12.5512 13.9539 11.8594 14.2401 11.0482 14.2401C10.2371 14.2401 9.54522 13.9539 8.97264 13.3813C8.40007 12.8087 8.11379 12.1169 8.11379 11.3057C8.11379 10.4946 8.40007 9.80272 8.97264 9.23015C9.54522 8.65758 10.2371 8.37129 11.0482 8.37129C11.5254 8.37129 11.9786 8.49058 12.4081 8.72915L15.1994 6.00944C15.3425 5.81858 15.5334 5.72315 15.7719 5.72315C16.0105 5.72315 16.2014 5.81858 16.3445 6.00944ZM12.3365 11.3057C12.3365 10.9717 12.2053 10.6854 11.9429 10.4469C11.6804 10.2083 11.3822 10.089 11.0482 10.089C10.7142 10.089 10.416 10.2083 10.1536 10.4469C9.89114 10.6854 9.75993 10.9717 9.75993 11.3057C9.75993 11.6397 9.89114 11.9379 10.1536 12.2004C10.416 12.4628 10.7142 12.594 11.0482 12.594C11.3822 12.594 11.6804 12.4628 11.9429 12.2004C12.2053 11.9379 12.3365 11.6397 12.3365 11.3057Z"
                    fill="#DE542E"
                  />
                </svg>
                <div className="text-[14px]">
                  {car.mileage}
                  <span className="text-[#000000]"> Miles</span>
                </div>
              </div>
            )}

            {(car?.fuelType?.name || car?.fuelType) && (
              <div className="flex flex-col items-center">
                <svg
                  width="25"
                  height="25"
                  viewBox="0 0 21 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12.2732 4.45221C12.2732 4.26529 12.2148 4.11341 12.0979 3.99658C11.9811 3.87975 11.8292 3.82134 11.6423 3.82134H5.05317C4.86624 3.82134 4.71436 3.87975 4.59754 3.99658C4.48071 4.11341 4.42229 4.26529 4.42229 4.45221V9.7095C4.42229 9.89642 4.48071 10.06 4.59754 10.2002C4.71436 10.3404 4.86624 10.4105 5.05317 10.4105H11.6423C11.8292 10.4105 11.9811 10.3404 12.0979 10.2002C12.2148 10.06 12.2732 9.89642 12.2732 9.7095V4.45221ZM10.9413 9.07862H5.68404V5.15318H10.9413V9.07862ZM19.8437 5.15318L17.18 3.89143C17.0398 3.79797 16.8762 3.77461 16.6893 3.82134C16.5024 3.86807 16.3739 3.97322 16.3038 4.13677C16.2337 4.30033 16.222 4.47558 16.2687 4.6625C16.3154 4.84943 16.4323 4.96626 16.6192 5.01299L17.6005 5.57376L17.5305 5.78406C17.5305 6.20464 17.659 6.57849 17.916 6.90561C18.173 7.23273 18.4884 7.46639 18.8623 7.60658V15.5977C18.8623 15.7846 18.7922 15.9481 18.652 16.0883C18.5118 16.2285 18.3599 16.2986 18.1964 16.2986C18.0328 16.2986 17.8809 16.2285 17.7407 16.0883C17.6005 15.9481 17.5305 15.7846 17.5305 15.5977V10.3404C17.5305 9.59267 17.2617 8.91506 16.7243 8.30755C16.1869 7.70004 15.5911 7.32619 14.9369 7.186V3.12037C14.9369 2.4194 14.6798 1.81189 14.1658 1.29784C13.6517 0.783796 13.0442 0.526773 12.3433 0.526773H4.42229C3.72132 0.526773 3.10213 0.783796 2.56472 1.29784C2.02731 1.81189 1.7586 2.4194 1.7586 3.12037V17.8408L0.84734 18.3314C0.566952 18.4716 0.426758 18.6586 0.426758 18.8922V20.8549C0.426758 21.0419 0.496855 21.2054 0.637049 21.3456C0.777243 21.4858 0.940803 21.5559 1.12773 21.5559H15.5677C15.7547 21.5559 15.9065 21.4858 16.0234 21.3456C16.1402 21.2054 16.1986 21.0419 16.1986 20.8549V18.8922C16.1986 18.6586 16.0818 18.4716 15.8481 18.3314L14.9369 17.8408V8.51784C15.264 8.65804 15.5561 8.90338 15.8131 9.25386C16.0701 9.60435 16.1986 9.96652 16.1986 10.3404V15.5977C16.1986 16.1584 16.3972 16.6374 16.7944 17.0346C17.1917 17.4319 17.659 17.6305 18.1964 17.6305C18.7338 17.6305 19.2011 17.4319 19.5983 17.0346C19.9955 16.6374 20.1941 16.1584 20.1941 15.5977V5.78406C20.1941 5.50367 20.0773 5.29338 19.8437 5.15318ZM14.9369 20.2241H1.7586V19.3128L2.73996 18.8221C2.97362 18.7287 3.09045 18.5417 3.09045 18.2613V3.12037C3.09045 2.79325 3.21896 2.50117 3.47598 2.24415C3.733 1.98713 4.04844 1.85862 4.42229 1.85862H12.3433C12.6704 1.85862 12.9625 1.98713 13.2195 2.24415C13.4765 2.50117 13.605 2.79325 13.605 3.12037V18.2613C13.605 18.5417 13.7218 18.7287 13.9555 18.8221L14.9369 19.3128V20.2241Z"
                    fill="#E95923"
                  />
                </svg>
                <div className="text-[14px]">
                  {car?.fuelType?.name || car?.fuelType}
                </div>
              </div>
            )}

            {(car?.cylinders || car?.cylinder?.name) && (
              <div className="flex flex-col items-center">
                <svg
                  width="25"
                  height="25"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12.457 0.950806C12.7675 0.900404 13.1356 0.947801 13.4316 1.0885L13.5537 1.15393L13.5547 1.15491C13.5729 1.16644 13.6001 1.18834 13.6289 1.21252C13.6604 1.23892 13.702 1.27481 13.751 1.31799C13.8491 1.4044 13.9818 1.52357 14.1416 1.66956C14.4618 1.96204 14.8929 2.36249 15.3779 2.82092C16.119 3.52005 16.5373 3.91872 16.7812 4.16467C17.0226 4.40802 17.1024 4.51162 17.1572 4.61877H17.1582C17.3175 4.9213 17.3707 5.25525 17.3174 5.56311V5.56409C17.2974 5.67363 17.2833 5.77181 17.2354 5.87854C17.1871 5.98577 17.1078 6.09714 16.9668 6.25354C16.6862 6.56477 16.1309 7.08931 14.999 8.17053C14.093 9.03665 13.5668 9.53466 13.2441 9.82776C12.9244 10.1182 12.7957 10.2161 12.6885 10.2711L12.6875 10.2721C12.3131 10.4575 11.9327 10.5024 11.5527 10.4078H11.5518C11.3648 10.3589 11.2266 10.3081 11.0586 10.194C10.8961 10.0835 10.7071 9.91426 10.4131 9.63733L9.93066 9.18323L5.67578 13.236L5.71973 13.319C5.89155 13.6472 5.958 13.9677 5.95801 14.4039C5.95801 14.6433 5.94166 14.8309 5.89844 15.0104C5.8552 15.1899 5.78599 15.3561 5.68555 15.5543C5.36125 16.1923 4.77273 16.6737 4.06152 16.8922L3.91699 16.9332C3.54003 17.0251 2.93667 17.0251 2.56055 16.9332H2.55957C0.891788 16.5087 0.0220905 14.7241 0.797852 13.2457C0.869339 13.1059 0.92617 13.0086 0.99707 12.9147C1.06718 12.8218 1.14932 12.7351 1.26855 12.6149C1.62784 12.2512 2.02586 12.0272 2.5293 11.8883L2.53125 11.8873C2.71233 11.8409 2.97012 11.8157 3.21973 11.8121C3.4683 11.8086 3.72409 11.8265 3.89844 11.8746H3.89746L4.04297 11.9127L4.15137 11.8239C4.24218 11.7451 5.25349 10.7944 6.41602 9.68713L8.44238 7.75745L8.07324 7.40002C7.83771 7.17256 7.68455 7.012 7.58105 6.86682C7.47439 6.71715 7.42205 6.58559 7.38086 6.42053C7.28889 6.04772 7.36459 5.59473 7.56348 5.27405L7.56543 5.27014L7.63867 5.18713C7.67301 5.15125 7.72035 5.10355 7.77832 5.04553C7.89465 4.92911 8.05601 4.76921 8.25195 4.57874C8.64409 4.19753 9.17342 3.69041 9.74609 3.14807C10.6974 2.24512 11.2225 1.749 11.5322 1.46936C11.8361 1.19501 11.9444 1.11794 12.0566 1.0719L12.2578 0.997681C12.3293 0.97564 12.4012 0.958727 12.457 0.950806ZM3.86816 12.7008C3.30262 12.5173 2.68442 12.5876 2.20312 12.8951V12.8961C2.09191 12.9661 1.95659 13.0832 1.83398 13.2145C1.71095 13.3462 1.60911 13.4835 1.55664 13.5865H1.55566C1.12727 14.4143 1.39579 15.3796 2.2041 15.8971C2.37634 16.0067 2.52312 16.0768 2.68164 16.1198C2.84101 16.1629 3.01778 16.1803 3.25098 16.1803C3.48396 16.1803 3.66085 16.163 3.82031 16.1198C3.9787 16.0768 4.12553 16.0069 4.29688 15.8981C4.84278 15.543 5.14355 15.016 5.14355 14.403C5.14352 13.9313 4.97839 13.5197 4.64941 13.1871V13.1862C4.40921 12.9458 4.1788 12.8017 3.86816 12.7008ZM6.94238 10.3102C6.34527 10.8789 5.80349 11.3982 5.41016 11.777C5.21362 11.9662 5.05413 12.1201 4.94434 12.2281C4.91419 12.2578 4.88747 12.284 4.86523 12.3063C4.86812 12.3085 4.87106 12.3108 4.87402 12.3131C4.92109 12.3501 4.98171 12.4006 5.04297 12.4557L5.18359 12.5729L7.27246 10.5846L9.33594 8.60706L9.19336 8.47131L9.03418 8.32483L6.94238 10.3102ZM10.416 3.64709C10.3569 3.70174 10.272 3.78081 10.168 3.87854C9.95999 4.07396 9.67519 4.34303 9.36621 4.63733C8.82747 5.15002 8.54745 5.41835 8.39551 5.57874C8.31958 5.65889 8.27992 5.70748 8.25684 5.7428C8.23597 5.77474 8.22799 5.79797 8.21777 5.83167C8.18748 5.93364 8.17482 6.00125 8.1748 6.06506C8.1748 6.12905 8.18731 6.19702 8.21777 6.29944H8.2168C8.2263 6.33008 8.23569 6.35197 8.26367 6.39124C8.29531 6.4356 8.35074 6.50047 8.45801 6.61096C8.67215 6.83151 9.07497 7.2178 9.85254 7.96155C10.7477 8.81729 11.1883 9.23811 11.4531 9.4469C11.5845 9.55046 11.6627 9.59456 11.7246 9.61487C11.7851 9.63471 11.8377 9.63439 11.9336 9.6344C12.0292 9.6326 12.0879 9.63122 12.1465 9.61487C12.2035 9.5989 12.2684 9.56737 12.3652 9.4967C12.5646 9.3511 12.8705 9.06179 13.4854 8.4762L14.498 7.51038L12.4727 5.58069H12.4717C11.8925 5.02546 11.3629 4.52134 10.9775 4.15588C10.7849 3.97322 10.6279 3.82517 10.5195 3.72229C10.4836 3.68819 10.4527 3.65922 10.4277 3.63538C10.4238 3.63898 10.4202 3.64323 10.416 3.64709ZM11.0215 3.0592L15.0967 6.94006L15.5732 6.48596L13.8428 4.84436C12.8374 3.89307 11.8825 2.98426 11.7158 2.83167H11.7148L11.4863 2.61682L11.0215 3.0592ZM13.0518 1.80042C12.7632 1.6634 12.3943 1.73475 12.1445 1.99084L12.1416 1.99475L12.1406 1.99377L12.083 2.04846L16.1729 5.91858L16.2471 5.8512C16.3449 5.76136 16.4081 5.6799 16.4473 5.59631C16.4861 5.51341 16.5049 5.42174 16.5049 5.30823C16.5049 5.21219 16.5042 5.16432 16.4854 5.10999C16.4662 5.05509 16.424 4.98432 16.3184 4.86292C16.1047 4.61744 15.6706 4.20755 14.7783 3.36487C14.1843 2.8031 13.7736 2.41972 13.498 2.17053C13.3602 2.04585 13.2575 1.95535 13.1826 1.89417C13.1454 1.86374 13.1161 1.84155 13.0938 1.82581C13.0828 1.81809 13.074 1.81219 13.0674 1.80823C13.0616 1.80478 13.0584 1.80273 13.0576 1.80237L13.0518 1.80042Z"
                    fill="#E95923"
                    stroke="#E95923"
                    stroke-width="0.233657"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10.9362 6.18644C10.6721 6.30489 10.4541 6.63123 10.4541 6.88384C10.4541 7.16425 10.6988 7.49058 10.981 7.59936C11.6169 7.84351 12.2165 7.17392 11.9355 6.53937C11.7623 6.18644 11.2899 6.0136 10.9362 6.18644Z"
                    fill="#E95923"
                  />
                </svg>

                <div className="text-[14px]">
                  {car?.cylinders || car?.cylinder?.name}
                </div>
              </div>
            )}

            {(car?.engineSize || car?.engine?.name) && (
              <div className="flex flex-col items-center">
                <svg
                  width="25"
                  height="25"
                  viewBox="0 0 22 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_1730_33991)">
                    <path
                      d="M13.1456 6.40362V4.6354H15.0927V2.86719H9.64062V4.6354H11.5878V6.40362"
                      stroke="#E95923"
                      strokeWidth="0.934628"
                      strokeMiterlimit="10"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4.96705 12.1496H3.40931V9.49731H1.85156V16.5702H3.40931V13.9179H4.96705"
                      stroke="#E95923"
                      strokeWidth="0.934628"
                      strokeMiterlimit="10"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M18.9865 9.05426V11.2645H17.8182V7.6587C17.8182 7.20913 17.5024 6.84399 17.1137 6.84399H9.2467C9.06016 6.84399 8.88024 6.93019 8.74822 7.08359L6.91398 9.49632H5.74567C5.35623 9.49632 4.9668 9.93837 4.9668 10.3804V15.6851C4.9668 16.1351 5.35701 16.5692 5.74567 16.5692H6.91398L8.749 18.9832C8.88102 19.1357 9.06016 19.2215 9.2467 19.2215H14.2529C14.4394 19.2215 14.6186 19.1357 14.7506 18.9832L17.6114 15.6758C17.7442 15.5228 17.8178 15.316 17.8178 15.0998V13.9169H18.9861V16.1271H20.5439V9.05426H18.9865Z"
                      stroke="#E95923"
                      strokeWidth="0.934628"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M13.9237 12.5879L10.8082 17.4505L11.9765 13.4721H10.0293L11.9765 9.05151H13.9237L11.9765 12.5879H13.9237Z"
                      fill="#E95923"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_1730_33991">
                      <rect
                        width="21.0291"
                        height="21.0291"
                        fill="white"
                        transform="matrix(1 0 0 -1 0.683594 21.5559)"
                      />
                    </clipPath>
                  </defs>
                </svg>

                <div className="text-[14px]">
                  {car?.engineSize || car?.engine?.name}
                </div>
              </div>
            )}
          </div>

          {/* Bidding Section */}

          <div className="flex justify-between items-center text-sm mt-2 border-t">
            {/* Bidding Info */}
            <div className="flex flex-col text-[14px] text-[#B1B1B1] gap-1">
              {/* Start Bidding - single row */}
              <div className="flex items-center text-[12px] text-[#B1B1B1]">
                Start Bidding:
                <span className="text-[#00000] font-bold line-through text-[11px] px-2">
                  {formatPrice(car?.startingBid, "line-through", "11")}
                </span>
              </div>

              {/* Current Bidding - single row */}
              <div className="flex items-center text-[14px] text-[#000000] ">
                Current Bidding:
                <span className="text-[#e55a2b] text-[16px] font-[400] px-2 font-bold">
                  {formatPrice(
                    car?.currentBid || car?.startingBid,
                    "orange-600"
                  )}
                </span>
              </div>
            </div>

            {/* Bid Button */}
            <button className="flex bg-primary-dark text-white text-sm font-semibold px-4 py-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
              >
                <path
                  d="M16.1667 6.52451L13.6836 4.05579C14.3547 3.52201 15.2942 3.58873 15.9653 4.18923C16.3009 4.52284 16.5022 4.9899 16.5022 5.45696C16.5022 5.79057 16.368 6.1909 16.1667 6.52451Z"
                  stroke="white"
                  strokeWidth="0.835"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12.6113 10.2575L3.9541 19.3985C3.283 20.0657 2.20924 20.0657 1.53814 19.3985C0.867036 18.7313 0.799926 17.597 1.47103 16.9298L10.1282 7.78882L12.6113 10.2575Z"
                  stroke="white"
                  strokeWidth="0.835"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M17.308 7.65819L13.7511 11.3946L8.98633 6.59063L12.5432 2.9209L17.308 7.65819Z"
                  stroke="white"
                  strokeWidth="0.835"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7.37666 6.98833C7.04111 6.65472 7.04111 6.12094 7.37666 5.78733L11.6717 1.25021C12.0073 0.916597 12.6112 0.916597 12.9468 1.25021C13.2823 1.58382 13.2823 2.1176 12.9468 2.45121L8.65175 6.98833C8.3162 7.32194 7.71221 7.32194 7.37666 6.98833Z"
                  stroke="white"
                  strokeWidth="0.835"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13.3493 13.0611C13.0138 12.7275 13.0138 12.1937 13.3493 11.8601L17.6444 7.32296C17.9799 6.98935 18.5839 6.98935 18.9195 7.32296C19.255 7.65657 19.255 8.19035 18.9195 8.52397L14.6244 12.9944C14.2889 13.3947 13.752 13.3947 13.3493 13.0611Z"
                  stroke="white"
                  strokeWidth="0.835"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M20.4636 20.9999H10.1287C9.86024 20.9999 9.5918 20.733 9.5918 20.4661V19.5987C9.5918 19.3318 9.86024 19.0649 10.1287 19.0649H20.4636C20.7321 19.0649 21.0005 19.3318 21.0005 19.5987V20.4661C21.0005 20.733 20.7992 20.9999 20.4636 20.9999Z"
                  stroke="white"
                  strokeWidth="0.835"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M19.9924 19.0678H10.6641V17.7333C10.6641 17.3997 10.9325 17.1328 11.2681 17.1328H19.3884C19.7239 17.1328 19.9924 17.3997 19.9924 17.7333V19.0678Z"
                  stroke="white"
                  strokeWidth="0.835"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Pre Bid
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AuctionCarCard;
